import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Image } from "@/components/ui/image";

/**
 * The banner's photo plate. It shows the experience's own photographs one at a
 * time, crossfading every four seconds, and stays a fixed square so the card
 * never changes height between shots. Auto-play stops on hover, on keyboard
 * focus and under a reduced-motion preference; a single photograph renders on
 * its own with no controls at all.
 */
const HOUSE_TILE =
  "absolute inset-0 bg-[radial-gradient(120%_140%_at_0%_0%,#2b2320_0%,#171717_55%,#0d0d0d_100%)]";

const ARROW =
  "absolute top-1/2 z-20 grid h-5 w-5 -translate-y-1/2 place-items-center rounded-full bg-white/90 text-[#171717] opacity-0 shadow-[0_2px_6px_rgba(0,0,0,.28)] transition-opacity duration-200 hover:bg-white focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white";

export default function AdSlideshow({ photos = [], icon: Icon, alt = "", compact = false }) {
  const list = (photos || []).filter(Boolean);
  const count = list.length;
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reduced, setReduced] = useState(false);
  const touchStart = useRef(0);

  const frame = count
    ? compact
      ? "h-[58px] w-[58px]"
      : "h-[68px] w-[68px] sm:h-[84px] sm:w-[84px]"
    : compact
      ? "h-[58px] w-[74px]"
      : "h-[68px] w-[92px] sm:h-[84px] sm:w-[104px]";

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReduced(query.matches);
    sync();
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    if (count > 1 && index > count - 1) setIndex(0);
  }, [count, index]);

  useEffect(() => {
    if (count < 2 || paused || reduced) return undefined;
    const timer = window.setInterval(() => setIndex((current) => (current + 1) % count), 4000);
    return () => window.clearInterval(timer);
  }, [count, paused, reduced]);

  const step = (delta) => setIndex((current) => (current + delta + count) % count);

  if (!count) {
    return (
      <span aria-hidden="true" className={`relative shrink-0 overflow-hidden rounded-[9px] bg-[#efeee9] dark:bg-white/[0.06] ${frame}`}>
        <span className={HOUSE_TILE} />
        {Icon ? <Icon className="absolute left-1/2 top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 text-white" /> : null}
      </span>
    );
  }

  return (
    <span
      className={`group/slide relative shrink-0 overflow-hidden rounded-[9px] bg-[#efeee9] dark:bg-white/[0.06] ${frame}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      onTouchStart={(event) => {
        touchStart.current = event.touches[0].clientX;
      }}
      onTouchEnd={(event) => {
        const delta = event.changedTouches[0].clientX - touchStart.current;
        if (Math.abs(delta) > 26) step(delta < 0 ? 1 : -1);
      }}
    >
      {list.map((url, position) => (
        <span
          key={`${url}-${position}`}
          aria-hidden={position === index ? undefined : "true"}
          className={`absolute inset-0 transition-opacity duration-700 ease-out ${position === index ? "opacity-100" : "opacity-0"}`}
        >
          <Image
            src={url}
            alt={position === index ? alt : ""}
            fittingType="fill"
            quality={76}
            loading={position === 0 ? "eager" : "lazy"}
            className="h-full w-full object-cover"
          />
        </span>
      ))}

      {count > 1 && (
        <>
          <span aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-1/2 bg-gradient-to-t from-black/45 via-black/10 to-transparent" />

          <button
            type="button"
            aria-label={alt ? `Previous photo of ${alt}` : "Previous photo"}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              step(-1);
            }}
            className={`${ARROW} left-0.5 hover:opacity-100 group-hover/slide:opacity-100`}
          >
            <ChevronLeft aria-hidden="true" className="h-3 w-3" strokeWidth={3} />
          </button>
          <button
            type="button"
            aria-label={alt ? `Next photo of ${alt}` : "Next photo"}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              step(1);
            }}
            className={`${ARROW} right-0.5 hover:opacity-100 group-hover/slide:opacity-100`}
          >
            <ChevronRight aria-hidden="true" className="h-3 w-3" strokeWidth={3} />
          </button>

          <span className="absolute inset-x-0 bottom-0.5 z-20 flex items-center justify-center gap-0.5">
            {list.map((url, position) => (
              <button
                key={`dot-${url}-${position}`}
                type="button"
                aria-label={alt ? `Photo ${position + 1} of ${list.length}: ${alt}` : `Photo ${position + 1}`}
                aria-current={position === index ? "true" : undefined}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setIndex(position);
                }}
                className="grid h-3.5 w-3.5 place-items-center"
              >
                <span
                  className={`block rounded-full transition-all duration-300 ${
                    position === index ? "h-[5px] w-[5px] bg-white" : "h-[4px] w-[4px] bg-white/55"
                  }`}
                />
              </button>
            ))}
          </span>
        </>
      )}
    </span>
  );
}